<template>
  <full-text-template header-title="Legal Policy">
    <section>
      <h2 id="disclaimer">Disclaimer</h2>

      <p>
        You are solely responsible for your communications and interactions with
        other users and with other persons whom you communicate or interact with
        as a result of the Website or Services. You understand that UPchieve
        makes no representations or warranties as to the conduct of other users
        of the website. UPchieve encourages you to take reasonable precautions
        when using the app and to refrain from sharing personally identifiable
        information (such as your phone number, email address, full name, etc.).
      </p>
    </section>

    <section>
      <h2 id="contact">Contact us!</h2>

      <div class="highlight">
        If you have any questions, comments, or concerns about our Services,
        Terms of Service, Privacy Policy, or Disclaimer you may contact us at:
        <a href="mailto:support@upchieve.org">support@upchieve.org</a>.
      </div>
    </section>

    <section>
      <h2 id="terms">Terms of Service</h2>

      <p>
        <em>Date of Last Revision: March 9, 2022</em>
      </p>

      <p>
        These Terms of Service (“Terms” or “Terms of Service”) explain your
        rights and obligations in using app.upchieve.org (“Website”), owned and
        operated by UPchieve (“UPchieve” or “we” or “us”). We refer to our
        Website visitors as users ("Users"), whether as a guest, registered
        user, volunteer, or person seeking academic services for a student or
        him or herself, of UPchieve’s Services ("Services").
      </p>

      <p>
        These Terms, together with our Privacy Policy and Disclaimer, form the
        agreement with you regarding the use of the Website (the "User
        Agreement").
      </p>

      <p>
        <strong>
          Please read the Terms carefully. By continuing to use the Services,
          you agree that you have read and are bound by these Terms. If you do
          not agree to these Terms, you must not use the Website or engage in
          UPchieve’s Services.
        </strong>
      </p>

      <p>
        IF YOU ARE A PARENT OR GUARDIAN AND YOU PROVIDE CONSENT FOR YOUR CHILD
        TO REGISTER WITH THE WEBSITE, YOU AGREE TO BE BOUND BY THESE TERMS OF
        SERVICE IN RESPECT OF SUCH CHILD’S USE OF THE WEBSITE.
      </p>

      <p>
        These Terms include important agreements about your rights and the
        rights of UPchieve, including a binding agreement about how to resolve
        any disputes between us connected to the Services. See HOW WE WILL
        RESOLVE CONFLICTS BETWEEN US CONCERNING THE SERVICES.
      </p>

      <p>
        These Terms will also govern any dispute between us in connection with
        the Privacy Policy.
      </p>

      <div>
        <accordion-item label="Eligibility for Services">
          <p>
            THE WEBSITE IS NOT AVAILABLE TO ANY USERS PREVIOUSLY SUSPENDED OR
            REMOVED FROM THE WEBSITE BY UPCHIEVE. By using or registering an
            account for the Website, you represent (a) that you have read and
            agree to these Terms, the Privacy Policy, and Disclaimers; (b) that
            you have not been previously suspended or removed from the Website
            by UPchieve; and (d) that your registration and your use of the
            Website is in compliance with any and all applicable laws and
            regulations.
          </p>
        </accordion-item>

        <accordion-item label="Availability of Services">
          <p>
            <strong>DESCRIPTION OF SERVICES.</strong> THE WEBSITE AND SERVICES
            COMPRISE AN ONLINE PLATFORM WHICH FACILITATES ACADEMIC SERVICES. YOU
            UNDERSTAND AND AGREE THAT UPCHIEVE DISCOURAGES THE SHARING OF
            PERSONALLY IDENTIFIABLE INFORMATION BETWEEN USERS AND IS NOT A PARTY
            TO ANY AGREEMENTS ENTERED INTO BETWEEN USERS. AS MORE FULLY DETAILED
            BELOW AND TO THE FULLEST PERMITTED BY APPLICABLE LAW, UPCHIEVE
            DISCLAIMS ALL LIABILITY RELATED TO THE ACADEMIC SERVICES OR THE
            CONDUCT OF USERS OF THE WEBSITE. ACCORDINGLY, ANY MEETINGS (VIRTUAL
            OR OTHERWISE) BETWEEN USERS OR ANY OTHER THIRD PARTIES ARE DONE AT
            THE PARTIES' SOLE RISK
          </p>

          <p>
            <strong>SERVICES MAY INCLUDE INTERACTIVE ACCOUNTS.</strong> In using
            the Services, you may be provided with the opportunity to upload
            information, data, or content specific to you ("Account
            Information"). You may be provided with the opportunity to upload
            this Account Information on your username and password protected
            Account. Our provision of any such interactivity is a Service
            governed by these Terms. You are responsible for keeping your
            username and password secure. If you believe that there has been
            unauthorized access to your username, password or identity, please
            contact us as soon as possible so that we can work together to limit
            the damage. We have the right to disable any username or password,
            whether chosen by you or allocated by us, at any time, if in our
            reasonable opinion you have failed to comply with any of the
            provisions of these Terms.
          </p>

          <p>
            <strong>ONLINE SERVICES SUBJECT TO CHANGE.</strong> You agree that
            we may change or discontinue the Services in our sole discretion and
            without prior notice. You agree that you will not rely on the
            continued availability of the Services in taking any action,
            refraining from any action, or entering into any commitment.
          </p>

          <p>
            <strong>CONTENT NOT RETAINED FOREVER.</strong> You agree that any
            Account you may create via these Services may be terminated, and/or
            some or all of the associated Account Information deleted, following
            a period of inactivity. Such deletion may be done without prior
            notice.
          </p>

          <p>
            <strong>IF IMPORTANT, KEEP A COPY.</strong> You agree that, unless
            otherwise specified, any Account Information which you upload or
            provide via the Services may be modified or deleted without prior
            notice and at UPchieve’s sole discretion. Therefore, to the extent
            that any Account Information has importance to you, you agree to
            maintain an original copy separate from these Services.
          </p>
        </accordion-item>

        <accordion-item label="Your Responsibility When Using the Services">
          <p>
            <strong>YOUR LIABILITY.</strong> You are fully responsible for how
            you use our Services. You agree to indemnify and hold harmless us
            and our directors, officers, employees, service providers, vendors,
            and agents from and against any and all losses, liabilities, claims,
            damages or expenses (including attorneys' fees and court costs and
            expenses) arising from or related to any use of the Services by you
            or that occurs because of you.
          </p>

          <p>
            <strong>PROHIBITED USE OF THE SERVICES.</strong> You may not post,
            upload, email or otherwise transmit on UPchieve’s Website content of
            any kind that are, within the sole discretion of UPchieve,
            determined to be commercial, illegal, offensive or potentially
            harmful to others.
          </p>

          <p>
            <strong>YOU MUST KEEP YOUR EMAIL ADDRESS UP TO DATE.</strong>If you
            change your e-mail address or other contact information used by
            UPchieve to communicate with you electronically, you must notify
            UPchieve of the change immediately by logging on to the Website and
            updating your profile to reflect the correct email address or by
            sending written notice of your updated e-mail address to
            <a href="mailto:support@upchieve.org">support@upchieve.org</a>.
          </p>

          <p>
            If you do not update or change an incorrect e-mail address or other
            incorrect contact information, you understand and agree that any
            notices, statements or other communications sent to you by UPchieve
            will still be considered to have been provided to you if they were
            made available to you in electronic form on the Website or e-mailed
            to the e-mail address we have for you in our records. UPchieve
            reserves the right, if we choose, to restrict your ability to use
            the Services if we believe that the e-mail address you provided is
            incorrect.
          </p>
        </accordion-item>

        <accordion-item label="Prohibited Conduct">
          <p>YOU AGREE NOT TO:</p>
          <ol type="a">
            <li>
              Use the Website for any commercial use or purpose unless expressly
              permitted by UPchieve in writing, it being understood that the
              Website and related services are intended for personal,
              non-commercial use only;
            </li>

            <li>
              Post, upload, or distribute any defamatory, libelous, or
              inaccurate content;
            </li>

            <li>
              Post, upload, or distribute any other content that is unlawful or
              that a reasonable person could deem to be objectionable,
              offensive, indecent, pornographic, harassing, threatening,
              embarrassing, distressing, vulgar, hateful, discriminatory, or
              otherwise inappropriate;
            </li>

            <li>
              Impersonate any person or entity, falsely claim an affiliation
              with any person or entity, access the Website accounts of others
              without permission, or perform any other fraudulent activity;
            </li>

            <li>
              Send unsolicited offers, advertisements, proposals, junk mail, or
              spam to other Users of the Website (including, but not limited to,
              unsolicited advertising, promotional materials, or other
              solicitation material, bulk mailing of commercial advertising,
              chain mail, informational announcements, charity requests, and
              petitions for signatures);
            </li>

            <li>
              Use the Website for any illegal purpose, or in violation of any
              local, state, national, or international law, including, without
              limitation, laws governing intellectual property and other
              proprietary rights, and data protection and privacy; or
            </li>

            <li>
              Defame, harass, abuse, threaten or defraud Users of the Website;
              or
            </li>

            <li>
              Collect, or attempt to collect, personal information about Users
              of the Website or third parties.
            </li>
          </ol>
        </accordion-item>

        <accordion-item label="Privacy Notice">
          <p>
            Your privacy is important to UPchieve. Please read the UPchieve
            Privacy Policy, hereby incorporated into the Terms, carefully for
            information relating to UPchieve’s collection, use, and disclosure
            of your personal information.
          </p>
        </accordion-item>

        <accordion-item
          label="How We Will Resolve Conflicts Between Us Concerning the Services"
        >
          <p>
            <strong>DISPUTE RESOLUTION -- VENUE.</strong> You agree to be
            subject to the jurisdiction of New York. You agree that any dispute
            between you and us will be resolved in New York County, New York to
            the exclusion of any other potential venue.
          </p>

          <p>
            <strong>NO CLASS ACTIONS; NO JURY TRIAL.</strong> You agree that you
            will only sue us as an individual in any dispute connected with the
            Services. You agree that you will not file a class action or
            participate in a class action. You agree that you will not join any
            claim you may have against us with the claim of any third party. You
            waive any right to a trial by jury.
          </p>

          <p>
            <strong>BINDING ARBITRATION.</strong> You and we agree that, at the
            election of either party, any dispute connected with Services
            between us may be sent to binding individual (non-class) arbitration
            to be administered by JAMS, Inc. ("JAMS"). If, for any reason, JAMS
            is not available or will not hear the case, you or we may file our
            case with any national arbitration company. You and we agree that
            the arbitrator shall not have any power to consider, hear,
            entertain, or grant any class arbitration.
          </p>

          <p>
            <strong>STANDARD OF CARE.</strong> You agree that our sole
            obligation to you in connection with the Services is to provide the
            Services as-is. You agree that unless we do something that is
            grossly negligent or an act of willful misconduct in connection with
            the Services, we will not be liable to you or to any third party.
          </p>

          <p>
            <strong>LIMITATIONS ON LIABILITY.</strong> If, for any reason, we
            are judged liable to you in connection with the Services, you agree
            that your recovery will be limited to your actual damages, measured
            by actual out-of-pocket economic loss. You agree that you will not
            ask for any consequential, special, punitive or exemplary damages,
            or indirect or lost profits. You agree that even if you request
            these types of damages, a court or arbitrator cannot award them to
            you. You understand and agree that the price of these Services would
            be significantly higher but for these limitations on liability. This
            limitation on liability shall not be construed to apply to any
            damages or other substantive remedies available via statute.
          </p>

          <p>
            <strong>WE'RE NOT RESPONSIBLE FOR ALL CIRCUMSTANCES.</strong> You
            also agree that we are not responsible for anything beyond our
            control. For example, we are not liable: for Service interruptions
            caused by problems with the communications network; for problems
            caused by any Internet service provider; for your computer or its
            programs failing; for the acts of criminal attackers, whether in
            real-time or automated (e.g., viruses); for other technologically
            harmful material that may infect your computer equipment, computer
            programs, data or proprietary material due to your use of the
            Websites or to your downloading of any content on it, or on any
            website linked to it; or for errors in information provided to us
            upon which we reasonably rely. These are only examples. We assume no
            responsibility for the content of other websites linked on the
            Website. Such links should not be interpreted as an endorsement by
            us of those linked websites. We will not be liable for any loss or
            damage that may arise from your use of them.
          </p>
        </accordion-item>

        <accordion-item label="Changes to the Terms of Service">
          <p>
            <strong>TERMINATION BY US.</strong> We reserve the right to
            terminate this User Agreement in our sole discretion. We may
            terminate this User Agreement prior to sending you written
            notification. For example, we will terminate the account of any User
            who threatens or harasses any other User.
          </p>

          <p>
            <strong>MODIFICATIONS.</strong> UPchieve reserves the right, at our
            discretion, to change, modify, add, or remove portions of the Terms
            at any time. Please check the Terms and other sections of the User
            Agreement periodically for changes. Your continued use of the
            Website after the posting of changes constitutes your binding
            acceptance of such changes. For any material changes to the Terms,
            UPchieve will make reasonable effort to provide notice to you of
            such amended Terms, such as by an email notification to the address
            associated with your account or by posting a notice on the Website,
            and such amended terms will be effective against You on the earlier
            of (i) your actual notice of such changes and (ii) thirty days after
            UPchieve makes reasonable attempt to provide you such notice.
            Disputes arising under these Terms will be resolved in accordance
            with the version of the Terms in place at the time the dispute
            arose.
          </p>
        </accordion-item>
      </div>
    </section>

    <section>
      <h2 id="privacypolicy">Privacy Policy</h2>

      <p>
        <em>Date of Last Revision: May 12, 2017</em>
      </p>

      <p>
        UPchieve takes your privacy seriously. We know you trust us to help with
        your homework and college applications, and we want you to know that you
        can trust us to keep your personal information safe.
      </p>

      <p>
        The below Privacy Policy (“Policy”) explains UPchieve’s collection, use,
        and disclosure of your personal information in connection with our
        website, app.upchieve.org (“Website”). We refer to our Website visitors
        as users ("Users"), whether as a guest, registered user, volunteer, or
        person seeking academic services for a student or him or herself, of
        UPchieve’s Services ("Services").
      </p>

      <p>
        <strong>
          Please read the Policy carefully. By continuing to use the Services,
          you agree that you have read and are bound by this Policy. If you do
          not agree to this Policy, you must not use the Website or engage in
          UPchieve’s Services.
        </strong>
      </p>

      <div>
        <accordion-item label="Why We Need Your Personal Information">
          <p>
            UPchieve strives to connect Users (in particular, students and
            volunteers) in order to help students succeed in their classes and
            in the college application process. However, in order to help us
            improve our Services and obtain funding to continue providing these
            Services to you, we have to collect personal information about you.
            Here are some examples of what we use your personal information to
            do:
          </p>

          <ul>
            <li>
              Store chat logs and whiteboard pictures from your sessions for
              screening purposes
            </li>
            <li>
              Contact you about your account, including, but not limited to,
              changes to the Terms of Service or UPchieve's Services
            </li>
            <li>
              Ask you for your feedback to help make our Services better
            </li>
            <li>Respond to your requests for information or assistance</li>
            <li>
              Consolidate anonymized information about Users in order to apply
              for and maintain funding
            </li>
            <li>
              If you were referred by a partner organization, provide that
              partner organization with information on your usage of the
              Services
            </li>
          </ul>

          <p>
            We also collect your name and email address when you log in to our
            site using Google SSO, which helps us to create and manage your
            account, offering a more seamless user experience.
          </p>

          <p>
            Because we care about your privacy, there are several things we
            promise we will never do:
          </p>

          <ul>
            <li>We WILL NOT sell your personal information to anyone</li>
            <li>We WILL NOT send you promotional mail or advertisements</li>
            <li>
              We WILL NOT give anyone except key staff members (defined below)
              and in special cases relevant federal authorities (explained
              below) access to personally identifiable information, such as your
              full name, gender, age, high school, or contact information
            </li>
          </ul>
        </accordion-item>

        <accordion-item label="What Personal Information We Collect">
          <p>
            Information we collect from you may include, but is not limited to,
            your:
          </p>

          <ul>
            <li>
              <strong>Email address</strong>
            </li>
            <li>
              <strong>Full name</strong>
            </li>
            <li>
              <strong
                >Name and email address (when using Google SSO for
                sign-in)</strong
              >
            </li>
            <li>
              <strong>Birthdate</strong>
            </li>
            <li>
              <strong>School name (only for students)</strong>
            </li>
            <li>
              <strong>Zip code (only for students)</strong>
            </li>
            <li>
              <strong>ID (only for volunteers)</strong>
            </li>
            <li>
              <strong>Work status (only for volunteers)</strong>
            </li>
            <li>
              <strong>LinkedIn Profile (only for volunteers, optional)</strong>
            </li>
            <li>
              <strong>Cookies, see below for detail</strong>
            </li>
            <li>
              <strong
                >Account Data, including past chat logs, feedback, actions on
                site, etc</strong
              >
            </li>
          </ul>
        </accordion-item>

        <accordion-item
          label="How We Collect and Store Your Personal Information"
        >
          <p>We collect information about you in the following ways:</p>

          <ul>
            <li>
              <strong>Information you give us.</strong> You may give us personal
              information during account registration, through editing your
              account profile, or through feedback forms or contact forms on our
              Website.
            </li>

            <li>
              <strong>
                Information we get from your use of our Services.
              </strong>
              When you use our Website, we collect information including but not
              limited to: which Services you use, at what time you use the
              Services, and how long your interactions with other Users last.
            </li>
            <li>
              <strong>
                Information we collect automatically.
              </strong>
              When you use our Site, we also collect information and analytics
              about how you interact with our Site through technologies like
              cookies.
            </li>
          </ul>

          <p>
            Information collected is hosted in the U.S. and subject to U.S.
            state and federal law. Users who access our Services from other
            jurisdictions transfer their information to the United States, where
            data protection and privacy laws may be less stringent than the laws
            of the originating country.
          </p>

          <p>
            We also collect your personal information when you choose to log in
            to our site using Google SSO, a third-party service. When using
            Google SSO, you share with us certain information provided by
            Google, which currently includes your name and email address. Please
            note that the privacy policies of Google also apply.
          </p>
        </accordion-item>

        <accordion-item label="Opting Out of Communications">
          <p>
            With your consent, from time to time we will communicate with you
            concerning our Services, including requests for future support. By
            using Google SSO to log into our site, you give us consent to use
            your name and email for communications related to our Services. To
            opt-out of receiving these communications, you may click on the
            “unsubscribe” link in any email communications.
          </p>
        </accordion-item>

        <accordion-item
          label="Update, Correct, or Delete Your Personal Information"
        >
          <p>
            You can visit the Profile section of our Site at any time to see
            some of the personal information we have collected about you. You
            can update most of this information on the Site, but you may also
            email us at
            <a href="mailto:support@upchieve.org">support@upchieve.org</a> at
            any time to request that we update or remove your information.
            UPchieve will retain the information about you for as long as your
            account remains active. iOS app users may request full deletion of
            their account, which makes it inaccessible while we manually process
            removal of the data.
          </p>
        </accordion-item>

        <accordion-item label="Who Has Access to Your Personal Information">
          <p>
            Now you know how and why we collect information - but who actually
            has access to it? Don't worry - UPchieve gives other Users and
            UPchieve staff members limited access to your information. In
            particular:
          </p>

          <ul>
            <li>
              <strong>UPchieve Staff Members:</strong> Only two staff members,
              the Executive Director and the Director of Technology, will be
              able to access all of the information connected to your account.
              Other UPchieve staff members may receive your name, email address,
              and any additional information you submit, should you use one of
              the forms on our Website (such as the “Contact Us” form) in order
              to respond to your message. Members of UPchieve’s Operations team
              may access specific information connected with your account for
              purposes such as screening chat logs or consolidating information
              to provide to the partner organization that referred you (if any).
              Information collected through Google SSO, such as your name and
              email, may also be accessed by certain UPchieve staff members for
              account management and user support purposes.
            </li>

            <li>
              <strong>Other Users:</strong> Other Users may only view your first
              name and the help topic associated with the current session while
              you are in a session with them. After ending the session, the
              other User may no longer view any information about you or contact
              you. Providing personally identifiable information or contact
              information (such as your full name or your email address) to
              another User through the website is a breach of our Terms of
              Service. Furthermore, we strongly encourage all Users to follow
              internet safety practices and use caution even when interacting
              with other Users. For more information on best internet safety
              practices specifically for teens, check out
              <a
                href="https://teens.webmd.com/features/teen-internet-safety-tips"
                target="_blank"
                rel="noopener noreferrer"
                >WebMD's article</a
              >.
            </li>

            <li>
              <strong>Partner Organizations:</strong> If you were referred by a
              partner organization, we may share information regarding your
              usage of the app, such as how many times you used our Services,
              with that partner. This information will not be anonymized.
            </li>
          </ul>

          <p>
            <strong>
              Completely anonymized and consolidated information
            </strong>
            will be shared in our annual reports (which will be available on our
            Website to all visitors) and in our applications for funding.
            Anonymized and consolidated means that the information will not
            pertain to any one individual and it will not be possible to
            identify any individuals that may or may not be included. For
            example, we may share statistics such as X percent of students who
            used our services were the first in their family to attend college
            or Y percent of students said they thought our services were
            helpful. If you would like to be excluded from these statistics,
            please email
            <a href="mailto:support@upchieve.org">support@upchieve.org</a>.
          </p>

          <p>
            On occasion, we may also ask for your permission to quote feedback
            from one of your submitted feedback forms. However, we will never
            share anything from your feedback forms without receiving prior
            permission.
          </p>
        </accordion-item>

        <accordion-item
          label="We May Share Your Personal Information For Safety or Legal Reasons"
        >
          <p>
            If we have reason to believe that disclosing your personal
            information is required by law, is reasonable to protect someone
            from possible injury, or is required as part of an internal or
            external legal investigation, we may do so. This includes
            information collected through Google SSO. We will not share your
            name or email address obtained via Google SSO with any third
            parties, except as required by law or to protect our rights,
            property, or safety, as well as those of our users or the public.
          </p>
        </accordion-item>

        <accordion-item label="Cookies">
          <ul>
            <li>
              <strong>
                What a cookie is:
              </strong>
              <p>
                A Cookie is a small file that can be placed on your device that
                allows us to recognize and remember you. It is sent to your
                browser and stored on your computer’s hard drive. When you visit
                our sites, we may collect information from you automatically
                through cookies or similar technology, such as a tag, pixel, or
                other web beacon.
              </p>
            </li>
            <li>
              <strong>How we use cookies:</strong>
              <p>
                We use Cookies in a range of ways to improve your experience on
                our site, including by keeping you logged in, and providing us
                with an understanding of how you use and interact with our Site.
                Ultimately, this allows us to provide the Service that you
                receive on our Site and app.
              </p>
              <p>
                Other organizations also collect user information on our Site
                through Cookies, tags and pixels. Tags and pixels, also known as
                web beacons, are similar to Cookies but are collected through
                embedded images. Using Cookies and other technologies allows
                other organizations to help us to analyze how our site is being
                used, and measure the number of visitors to the site.
              </p>
              <p>
                We use third-party services, including Google Analytics, to
                gather general information about how visitors use our Site for
                website optimization and analytics. To find out more about
                Google Analytics' privacy policies, please visit the following
                link: http://www.google.com/policies/privacy/partners/. We use a
                service called NewRelic to track errors and performance of our
                applications. It uses cookies on our site. We also use Google
                SSO as a part of our services, which utilizes cookies for its
                operation. For details about how Google uses cookies, please see
                their
                <a href="https://policies.google.com/technologies/cookies">
                  Cookie Policy</a
                >. For all of the above services, the only people with access to
                data are our staff members. While the data is sent to a third
                party to process, they are not allowed to view any of the data
                coming from our system.
              </p>
            </li>
            <li>
              <strong>To opt-out of cookies:</strong>
              <p>
                You may choose opt-out from the use of Cookies on your computer
                by selecting the appropriate settings in your browser. Be aware
                that opting-out may affect your ability to use all the features
                of the Site. Because a Cookie is stored on your computer, it is
                always possible for you to view, modify or delete it.
              </p>
              <p>
                It is also possible to stop your browser from accepting Cookies
                altogether by changing your browser’s Cookie settings. You can
                usually find these settings in the “Options” or “Preferences”
                menu of your browser. The following links may be helpful, or you
                can use the “Help” option in your browser.
              </p>
              <ul>
                <li>
                  <a
                    href="https://support.microsoft.com/en-gb/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09"
                    target="_blank"
                    >Cookie Settings in Edge</a
                  >
                </li>
                <li>
                  <a
                    href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer?redirectslug=Cookies&redirectlocale=en-US"
                    target="_blank"
                    >Cookie Settings in Firefox</a
                  >
                </li>
                <li>
                  <a
                    href="https://support.google.com/chrome/answer/95647?hl=en&ref_topic=14666"
                    target="_blank"
                    >Cookie Settings in Chrome</a
                  >
                </li>
                <li>
                  <a
                    href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac"
                    target="_blank"
                    >Cookie Settings in Safari</a
                  >
                </li>
              </ul>
            </li>
          </ul>
        </accordion-item>

        <accordion-item label="External Links">
          <p>
            Our Site may include links to external websites. UPchieve is not
            responsible for the privacy practices of these websites and we
            encourage you to review and understand their privacy practices and
            policies prior to providing any personal information to them.
          </p>
        </accordion-item>

        <accordion-item label="Changes to the Privacy Policy">
          <p>
            UPchieve reserves the right, at our discretion, to change, modify,
            add, or remove portions of the Policy at any time. For any material
            changes to the Policy, we will make a reasonable effort to provide
            notice to you of such changes. Your continued use of the Website
            after the posting of changes constitutes your binding acceptance of
            such changes.
          </p>
        </accordion-item>

        <accordion-item label="Contact Us">
          <p>
            If you have any questions about this Privacy Policy, please contact
            us at
            <a href="mailto:support@upchieve.org">support@upchieve.org</a>.
            Alternatively, please submit your question or request via the
            “Contact” feature on our Site.
          </p>
        </accordion-item>
      </div>
    </section>

    <section>
      <h2 id="volunteer">Volunteer Agreement</h2>

      <p>
        <em>Date of Last Revision: May 12, 2017</em>
      </p>

      <p>
        The below Volunteer Agreement (“Volunteer Agreement”) applies only to
        volunteers and covers your rights and obligations in connection with our
        website, app.upchieve.org (“Website”). We refer to our Website visitors
        who select "Volunteer" during account creation as volunteers
        ("Volunteers").
      </p>

      <p>
        <strong>
          Please read the Volunteer Agreement carefully. By continuing to use
          the Website, you agree that you have read and are bound by this
          Volunteer Agreement. If you do not agree to this Volunteer Agreement,
          you must not use the Website.
        </strong>
      </p>

      <div>
        <accordion-item label="Volunteer Capacity">
          <p>
            You have agreed to work as a volunteer for UPchieve and do so of
            your own volition, and any services you perform under this Agreement
            will be performed strictly in a volunteer capacity. You acknowledge
            that, as a volunteer, your time and service in this capacity are
            given without promise, expectation or receipt of any form of
            compensation, benefits or other remuneration by UPchieve or any
            other person or entity.
          </p>
        </accordion-item>

        <accordion-item label="Release">
          <p>
            You agree that you, your assignees, heirs, guardians and legal
            representatives will not make any claim against or sue UPchieve or
            its employees, officers, directors, agents or contractors for any
            injury or damage resulting from the negligence or other acts,
            however caused, by UPchieve or any of its employees, officers,
            directors, agents, contractors, recipients of services provided by
            UPchieve, or any other person in connection with your work as a
            volunteer for UPchieve. YOU HEREBY RELEASE AND FOREVER DISCHARGE
            UPCHIEVE AND ITS EMPLOYEES, OFFICERS, DIRECTORS, MEMBERS, AGENTS AND
            CONTRACTORS FROM ALL ACTIONS, CLAIMS OR DEMANDS THAT YOU, YOUR
            HEIRS, GUARDIANS AND LEGAL REPRESENTATIVES NOW HAVE OR MAY HAVE IN
            THE FUTURE, FOR ANY AND ALL INJURY OR DAMAGE RESULTING FROM YOUR
            WORK AS A VOLUNTEER FOR UPCHIEVE.
          </p>
        </accordion-item>

        <accordion-item label="Indemnification">
          <p>
            You agree to indemnify, defend and hold harmless UPchieve and its
            employees, officers, directors, agents, contractors and volunteers
            from any loss, liability, damage, or cost it may incur due to or in
            any way connected with your work as a volunteer for UPchieve,
            whether caused by the negligence of UPchieve or otherwise.
          </p>
        </accordion-item>

        <accordion-item label="Confidentiality">
          <p>
            In connection with your work as a volunteer for UPchieve, you
            understand and acknowledge that you may receive (without regard to
            intention, mode or medium of communication) information about the
            individuals to whom UPchieve provides services. You shall treat all
            such information as absolutely confidential, and will not disclose
            such information to any person other than as strictly necessary in
            connection with your work as a volunteer for UPchieve.
          </p>
        </accordion-item>

        <accordion-item label="Background Check">
          <p>
            You authorize UPchieve and/or any third party acting on UPchieve’s
            behalf to conduct or otherwise carry out an investigation of your
            background, references, character, past employment, consumer
            reports, driving history, credit history, education, criminal
            history and such other information that may be required in
            connection with your application for volunteerism.
          </p>
        </accordion-item>

        <accordion-item label="Property Rights">
          <p>
            You understand and agree that any materials or tools provided by or
            on behalf of UPchieve are and shall remain the property of UPchieve.
            No licenses or rights under any patent, copyright, trademark, trade
            name, trade secret or other intellectual property are granted or are
            implied to be granted to you or any other person by reason of this
            Agreement or your work as a volunteer for UPchieve.
          </p>
        </accordion-item>

        <accordion-item label="Termination">
          <p>
            You agree that UPchieve may terminate this Agreement, and/or your
            participation as a volunteer for UPchieve, at any time, without
            notice or cause.
          </p>
        </accordion-item>

        <accordion-item label="Interpretation">
          <p>
            Whenever possible, each provision of this Agreement will be
            interpreted in such manner as to be effective and valid under
            applicable law, but if any provision of this Agreement is held to be
            invalid, illegal, or unenforceable in any respect under any
            applicable law or rule, such invalidity, illegality or
            unenforceability will not affect any other provision or the
            enforcement of such provision, and such invalid, void or otherwise
            unenforceable provisions shall be null and void. To the fullest
            extent permitted by law, any invalid, void or otherwise enforceable
            provisions shall be automatically replaced by other provisions which
            are as similar as possible to such terms but are valid and
            enforceable.
          </p>
        </accordion-item>

        <accordion-item label="Jurisdiction & Waiver of Jury Trial">
          <p>
            This Agreement shall be governed and construed in accordance with
            the laws of the State of New York, without regard to the laws of
            conflict of laws. You agree that any dispute regarding the
            interpretation or validity of, or otherwise arising out of this
            Agreement, shall be subject to the exclusive jurisdiction of the
            federal or state courts located in New York, New York, and you
            hereby agree to submit to the personal and exclusive jurisdiction
            and venue of such courts and not to seek the transfer of any case or
            proceeding out of such courts. YOU HEREBY IRREVOCABLY WAIVE ANY AND
            ALL RIGHT TO TRIAL BY JURY IN ANY LEGAL PROCEEDING ARISING OUT OF OR
            RELATING TO THIS AGREEMENT.
          </p>
        </accordion-item>

        <accordion-item label="Amendments">
          <p>
            This Agreement represents the entire understanding and agreement
            relating to your work as a volunteer for UPchieve and the matters
            contained herein, and may only be amended, modified or waived by a
            separate writing executed by UPchieve and you expressly setting
            forth such amendment, modification or waiver.
          </p>
        </accordion-item>
      </div>
    </section>
  </full-text-template>
</template>

<script>
import { mapGetters } from 'vuex'

import FullTextTemplate from '@/components/FullTextTemplate.vue'
import AccordionItem from '@/components/AccordionItem.vue'

export default {
  name: 'LegalView',
  components: {
    FullTextTemplate,
    AccordionItem,
  },
  created() {
    if (!this.isAuthenticated) {
      this.$store.dispatch('app/hideNavigation')
    }
  },
  computed: {
    ...mapGetters({
      isAuthenticated: 'user/isAuthenticated',
    }),
  },
  watch: {
    isAuthenticated(isAuthed) {
      if (isAuthed) {
        this.$store.dispatch('app/showNavigation')
      } else {
        this.$store.dispatch('app/hideNavigation')
      }
    },
  },
}
</script>

<style lang="scss" scoped></style>
